/**
 * One set of terms of service: a name, who it applies to, and the sections.
 *
 * Its own flyout rather than the `<CatalogList>` generic form, because the
 * document is the whole point and a rich text editor does not fit in a stack
 * of labelled inputs. Everything else on the screen — the name, the divisions,
 * the default — is deliberately small and above the sections, so the writing
 * area is what the panel is mostly made of. `record-flyouts.md` §5 sizes the
 * catalog forms at 480; this one takes the record width instead, because 480
 * is a column to write a contract in.
 *
 * **Sections, since migration 0116** (`docs/proposal.md` §8.4, board 16 left).
 * The first version was one `RichEditor` holding the whole document, which
 * gave the customer an accordion — the reader split it at its headings — and
 * gave the author nothing: a wall of text, no way to say which part opens
 * first, and no way to reorder anything without retyping it. So the list is
 * the editor now: a row per section, **one expanded at a time**, and the
 * single `body` every other reader uses is compiled from them on the server.
 *
 * **A legacy document is offered a split and never given one.** Nothing here
 * writes until Save, which is board 16's own line, and there is no migration
 * and no background job that restructures a contract somebody's customers
 * have already signed.
 *
 * The editor arrives on demand (`<RichEditor>`); this file never imports
 * TipTap.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  EMPTY_TERMS, compileTermsBody, sanitizeTermsDoc, termsAreEmpty, termsToText, type TermsDoc,
} from '@rowted/shared'
import { Band, Button } from './primitives.js'
import { Sheet } from './sheet.js'
import { ChipsField, Field, FormSection, SwitchRow, TextInput } from './form/index.js'
import { RichEditor } from './rich-editor.js'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import {
  IconCheck, IconChevronDown, IconChevronRight, IconGrip, IconPlus, IconTrash,
} from '../frame/icons.js'
import type { Division } from '../lib/catalog-types.js'

/**
 * A section as the editor holds it.
 *
 * `id` is present on a row the server already has and **absent on a new one**
 * — the save sends it back untouched, which is what keeps a section's id
 * stable across an edit. A signed proposal froze these ids, so a reader
 * comparing the two finds the same section rather than a new row wearing the
 * same title.
 *
 * `key` is the client's own handle for the row, because React needs one
 * before the server has given it an id.
 */
interface SectionDraft {
  key: string
  id?: string
  title: string
  body: TermsDoc
  opensFirst: boolean
}

interface TermsDetail {
  id: string
  name: string
  body: TermsDoc
  isDefault: boolean
  allDivisions: boolean
  divisionIds: string[]
  active: boolean
  version: number
  /** Bumped by the server when the compiled words move, and by nothing else. */
  revision: number
  /** True once somebody has saved sections: from then on they are the truth. */
  sectioned: boolean
  sections: SectionDraft[]
}

/** The server sends sections without the client's row handle. */
interface SectionPayload {
  id?: string
  title: string
  body: TermsDoc
  opensFirst: boolean
}

const BLANK: TermsDetail = {
  id: '', name: '', body: EMPTY_TERMS, isDefault: false,
  allDivisions: true, divisionIds: [], active: true, version: 0,
  revision: 0, sectioned: true,
  sections: [{ key: 'new-1', title: '', body: EMPTY_TERMS, opensFirst: true }],
}

let counter = 0
const nextKey = (): string => `draft-${++counter}`

const draftOf = (section: SectionPayload): SectionDraft => ({
  ...section, body: sanitizeTermsDoc(section.body), key: nextKey(),
})

/**
 * The sections as the server will see them.
 *
 * **Every section is titled, and an untitled one falls back rather than
 * refusing** — the server's schema asks for 1–120 characters, and a save that
 * bounces because row four has no heading yet is a save somebody loses their
 * work to. "Section 4" is a title the author can see and fix; a 400 is not.
 *
 * One function because the save sends this *and* the footer compiles it to
 * work out whether the words moved — two spellings of "what is being saved"
 * would be two answers to what version this is.
 */
const payloadSections = (sections: SectionDraft[]): SectionPayload[] =>
  sections.map((one, index) => ({
    ...(one.id ? { id: one.id } : {}),
    title: one.title.trim() || `Section ${index + 1}`,
    body: one.body,
    opensFirst: one.opensFirst,
  }))

export function TermsEditor({
  termsId, divisions, onClose, onSaved,
}: {
  termsId: string | null
  divisions: Division[]
  onClose: () => void
  onSaved: (message: string) => void
}) {
  const [terms, setTerms] = useState<TermsDetail | null>(termsId ? null : { ...BLANK })
  const [saved, setSaved] = useState<TermsDetail | null>(termsId ? null : { ...BLANK })
  const [error, setError] = useState<string | null>(null)
  const [nameError, setNameError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** Which section is open. One at a time (§8.4); `null` is all collapsed. */
  const [openKey, setOpenKey] = useState<string | null>(null)
  /**
   * The split a legacy document would get, held beside the editor rather than
   * in it: the banner's **Split into N sections** is what moves it into the
   * list, and until then the list shows what the document is **today**.
   */
  const [offer, setOffer] = useState<SectionPayload[] | null>(null)

  useEffect(() => {
    if (!termsId) return
    void request<Omit<TermsDetail, 'sections'> & { sections?: SectionPayload[] }>(
      `/catalog-admin/terms/${termsId}`)
      .then((loaded) => {
        const body = sanitizeTermsDoc(loaded.body)
        const sent = loaded.sections ?? []

        /*
         * **Authored sections are the document; derived ones are an offer.**
         *
         * The server sends sections either way — its own rows when
         * `sectioned`, and `termsToSections(body)` when not — and the two
         * mean different things here. A legacy document opens as the one
         * block it actually is, with the derived split waiting behind the
         * banner, so nothing on the screen claims a structure nobody chose.
         */
        const authored = loaded.sectioned && sent.length > 0
        const block: SectionDraft = {
          key: nextKey(),
          title: loaded.name.trim() || 'Terms',
          body,
          opensFirst: true,
        }
        const shaped: TermsDetail = {
          ...loaded,
          body,
          sections: authored ? sent.map(draftOf) : [block],
        }
        setTerms(shaped)
        setSaved(shaped)
        setOffer(authored || sent.length < 2 ? null : sent)
        if (authored) setOpenKey(shaped.sections[0]?.key ?? null)
      })
      .catch((cause: Error) => setError(cause.message))
  }, [termsId])

  const changes = useMemo(() => {
    if (!terms || !saved) return 0
    return (Object.keys(terms) as (keyof TermsDetail)[])
      /*
       * `body` is derived from the sections on the server, so counting it
       * would count every section edit twice — and `revision` is the server's
       * answer to a save, never something somebody typed.
       */
      .filter((key) => key !== 'body' && key !== 'revision')
      .filter((key) => JSON.stringify(terms[key]) !== JSON.stringify(saved[key])).length
  }, [terms, saved])

  const patch = (next: Partial<TermsDetail>) => {
    setTerms((current) => (current ? { ...current, ...next } : current))
    if ('name' in next) setNameError(null)
  }

  const patchSections = (sections: SectionDraft[]) => patch({ sections })

  const patchSection = (key: string, next: Partial<SectionDraft>) => {
    setTerms((current) => {
      if (!current) return current
      return {
        ...current,
        sections: current.sections.map((one) => (one.key === key ? { ...one, ...next } : one)),
      }
    })
  }

  /**
   * At most one opens first, so setting it clears the others (§8.4).
   *
   * And **at least** one, which is why turning it off hands it to the top
   * section rather than leaving none: the customer's accordion has to open
   * something, the server picks the first when nothing is flagged, and a
   * switch that silently means "the first one, then" is one the screen should
   * say out loud.
   */
  const setOpensFirst = (key: string, on: boolean) => {
    setTerms((current) => {
      if (!current) return current
      const holder = on ? key : current.sections.find((one) => one.key !== key)?.key ?? key
      return {
        ...current,
        sections: current.sections.map((one) => ({ ...one, opensFirst: one.key === holder })),
      }
    })
  }

  const addSection = () => {
    if (!terms || terms.sections.length >= 30) return
    const one: SectionDraft = {
      key: nextKey(),
      title: '',
      body: EMPTY_TERMS,
      /* The first section a blank document gets is the one that opens. */
      opensFirst: terms.sections.length === 0,
    }
    patchSections([...terms.sections, one])
    setOpenKey(one.key)
  }

  /**
   * Take a section out of the draft.
   *
   * It is not a delete — nothing has been written yet, and the save's own
   * soft-replace is what stamps the row rule 2 keeps. Dropping the section
   * that opened first hands that to whatever is left at the top, because the
   * customer's accordion has to open *something*.
   */
  const removeSection = (key: string) => {
    if (!terms || terms.sections.length <= 1) return
    const left = terms.sections.filter((one) => one.key !== key)
    const orphaned = !left.some((one) => one.opensFirst)
    patchSections(orphaned
      ? left.map((one, index) => ({ ...one, opensFirst: index === 0 }))
      : left)
    if (openKey === key) setOpenKey(null)
  }

  const move = (from: string, onto: string) => {
    if (!terms || from === onto) return
    const order = [...terms.sections]
    const at = order.findIndex((one) => one.key === from)
    const to = order.findIndex((one) => one.key === onto)
    if (at < 0 || to < 0) return
    const [moved] = order.splice(at, 1)
    if (moved) order.splice(to, 0, moved)
    patchSections(order)
  }

  /** Fill the editor from the offer. Unsaved, like everything else here. */
  const takeOffer = () => {
    if (!offer) return
    const drafts = offer.map(draftOf)
    patchSections(drafts)
    setOffer(null)
    setOpenKey(drafts[0]?.key ?? null)
  }

  async function save() {
    if (!terms) return
    /*
     * The name is the only refusal. An empty document is a draft somebody is
     * coming back to, and a form that will not save a half-written contract
     * is a form somebody writes the contract outside of.
     */
    if (!terms.name.trim()) {
      setNameError('These need a name before they can be found again')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const body = {
        name: terms.name,
        /*
         * Sent, and ignored: `beforeWrite` compiles `body` from the sections
         * because one of the two is hand-written and the other derived, and
         * which is which is decided in one place. It stays in the payload
         * only because the schema asks for it.
         */
        body: terms.body,
        sections: payloadSections(terms.sections),
        isDefault: terms.isDefault,
        allDivisions: terms.allDivisions,
        divisionIds: terms.allDivisions ? [] : terms.divisionIds,
        active: terms.active,
        ...(termsId ? { version: terms.version } : {}),
      }
      await request(termsId ? `/catalog-admin/terms/${termsId}` : '/catalog-admin/terms',
        { method: termsId ? 'PUT' : 'POST', body })
      setSaved(terms)
      setOffer(null)
      onSaved(termsId ? `Saved ${terms.name}.` : `Added ${terms.name}.`)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  const written = terms?.sections.some((one) => !termsAreEmpty(one.body)) ?? false
  const name = terms?.name.trim() ? terms.name : 'New terms of service'

  /**
   * Whether this save would make a version — the **same comparison the server
   * makes**, run here so the button can say which save this is.
   *
   * It was `revision + 1` unconditionally, and the first thing it got wrong
   * was the commonest save there is: splitting a document at its own headings
   * compiles back to exactly the document that was there, so the button
   * promised version 2 and the save, correctly, made no version at all.
   */
  const moved = useMemo(() => {
    if (!terms || !saved) return false
    return JSON.stringify(compileTermsBody(payloadSections(terms.sections)))
      !== JSON.stringify(sanitizeTermsDoc(saved.body))
  }, [terms, saved])

  const nextVersion = (terms?.revision ?? 0) + 1
  const saveLabel = termsId
    ? moved ? `Save as version ${nextVersion}` : 'Save terms'
    : 'Save terms'

  return (
    <Sheet
      open
      onClose={onClose}
      kind="service"
      width={620}
      title={termsId ? name : 'New terms of service'}
      sub="Catalog · what a customer signs"
      dirty={changes > 0}
      changeCount={changes}
      saveLabel={saveLabel}
      onSave={() => void save()}
      /*
        * A quote carries these, so saying nothing is worth a word — but not a
        * refusal. Somebody drafting their terms over two sittings should be
        * able to save what they have.
        */
      /*
       * The version sentence is **in the panel**, not here: the footer note
       * and the paragraph above it were saying the same thing two inches
       * apart, which reads as two different warnings until you read both.
       */
      note={nameError
        ? <span className="text-danger">One field needs attention</span>
        : !written ? 'nothing written yet' : undefined}
      primary={(
        <Button
          variant="primary"
          className="h-[34px]"
          disabled={busy || !terms}
          onClick={() => void save()}
        >
          <IconCheck size={15} />
          {busy ? 'Saving…' : saveLabel}
        </Button>
      )}
    >
      {error && <Band tone="amber">{error}</Band>}

      {!terms
        ? <p className="m-0 text-[13px] text-text-muted">Loading…</p>
        : (
          <>
            <Field
              label="Name"
              required
              error={nameError ?? undefined}
              hint="what it is called in the picker on a quote"
            >
              {({ id, describedBy, invalid }) => (
                <TextInput
                  id={id}
                  describedBy={describedBy}
                  invalid={invalid}
                  value={terms.name}
                  placeholder="Standard terms"
                  onChange={(event) => patch({ name: event.target.value })}
                />
              )}
            </Field>

            <Field
              group
              label="Applies to"
              hint={terms.allDivisions
                ? 'Offered on every division, including ones added later.'
                : terms.divisionIds.length === 0
                  ? 'No division yet — nothing will offer these.'
                  : `Offered on ${terms.divisionIds.length} division`
                    + `${terms.divisionIds.length === 1 ? '' : 's'}.`}
            >
              {({ labelId }) => (
                <div className="flex flex-col gap-2">
                  <SwitchRow
                    label="Every division"
                    checked={terms.allDivisions}
                    onChange={(allDivisions) => patch({ allDivisions })}
                  />
                  {!terms.allDivisions && (
                    divisions.length === 0
                      ? (
                        <span className="text-[11px] text-text-faint">
                          No divisions yet. Settings → Divisions.
                        </span>
                      )
                      : (
                        <ChipsField
                          label="Applies to"
                          labelledBy={labelId}
                          options={divisions.map((one) => ({ value: one.id, label: one.name }))}
                          value={terms.divisionIds}
                          onChange={(divisionIds) => patch({ divisionIds })}
                        />
                      )
                  )}
                </div>
              )}
            </Field>

            <FormSection
              title="Sections"
              sub="the customer opens one at a time, in this order"
            />

            {offer && (
              <LegacyOffer
                count={offer.length}
                titles={offer.map((one) => one.title)}
                onSplit={takeOffer}
                onKeep={() => setOffer(null)}
              />
            )}

            <div role="list" aria-label="Sections" className="flex flex-col gap-2">
              {terms.sections.map((section, index) => (
                <SectionRow
                  key={section.key}
                  section={section}
                  number={index + 1}
                  open={openKey === section.key}
                  onOpen={() => setOpenKey(openKey === section.key ? null : section.key)}
                  onTitle={(title) => patchSection(section.key, { title })}
                  onBody={(body) => patchSection(section.key, { body })}
                  onOpensFirst={(on) => setOpensFirst(section.key, on)}
                  lone={terms.sections.length === 1}
                  onRemove={terms.sections.length > 1
                    ? () => removeSection(section.key)
                    : undefined}
                  onMove={move}
                />
              ))}
            </div>

            {terms.sections.length < 30 && (
              <button
                type="button"
                onClick={addSection}
                /*
                 * `shrink-0` because the `Sheet`'s body is a flex column: at
                 * 390px the panel overflows and a flex item with a height and
                 * the default `flex-shrink: 1` is squashed — this one went
                 * from 36px to 21, on the width where it matters most.
                 */
                className="flex h-9 w-full shrink-0 items-center justify-center gap-1.5
                           rounded-field border border-dashed border-line-strong text-[12.5px]
                           font-semibold text-text-muted hover:border-accent hover:text-accent"
              >
                <IconPlus size={14} />
                Add section
              </button>
            )}

            <FormSection title="Where they are used" />
            <div>
              <SwitchRow
                label="Use on new quotes"
                sub="one set can be the default — turning this on takes it off whichever set has it now"
                checked={terms.isDefault}
                onChange={(isDefault) => patch({ isDefault })}
              />
              <SwitchRow
                label="Offered"
                sub="off, and it stays on the quotes that already carry it but is not offered on a new one"
                checked={terms.active}
                onChange={(active) => patch({ active })}
              />
            </div>

            {termsId && (
              <p className="m-0 text-[11.5px] text-text-faint">
                {moved
                  ? `Saving makes version ${nextVersion}.`
                  : `The words have not changed, so this stays version ${terms.revision}.`}
                {' '}
                Proposals already signed keep the version they were signed on.
              </p>
            )}
          </>
        )}
    </Sheet>
  )
}

/**
 * The amber banner over a document nobody has sectioned (board 16, cell 2).
 *
 * An offer, with both answers on it and neither of them taken until Save.
 * The side-by-side is drawn from the same function the customer's accordion
 * reads with, so what it shows is exactly what they would get.
 */
function LegacyOffer({ count, titles, onSplit, onKeep }: {
  count: number
  titles: string[]
  onSplit: () => void
  onKeep: () => void
}) {
  return (
    <div className="rounded-record border border-warn bg-warn-bg p-3.5">
      <p className="m-0 text-[12.5px] leading-[1.45] text-text-body">
        This version was saved before sections. We found {count} heading
        {count === 1 ? '' : 's'}. Split them into sections so customers can open one at a
        time. Nothing is lost, and nothing is saved until you choose Save.
      </p>

      <div className="mt-3 grid grid-cols-2 gap-2.5">
        <Preview label="Today" rows={['One block']} />
        <Preview label={`After · ${count} sections`} rows={titles} />
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="primary" className="h-[34px]" onClick={onSplit}>
          Split into {count} sections
        </Button>
        <Button className="h-[34px]" onClick={onKeep}>Keep as one block</Button>
      </div>
    </div>
  )
}

function Preview({ label, rows }: { label: string; rows: string[] }) {
  return (
    <div className="rounded-field border border-line bg-surface p-2">
      <div className="pb-1 text-[10.5px] font-semibold uppercase tracking-[0.05em]
                      text-text-faint">
        {label}
      </div>
      <ul className="m-0 flex list-none flex-col gap-1 p-0">
        {rows.slice(0, 6).map((row, index) => (
          <li key={`${row}-${index}`} className="truncate text-[11.5px] text-text-body">
            {row || 'Untitled'}
          </li>
        ))}
        {rows.length > 6 && (
          <li className="text-[11.5px] text-text-faint">and {rows.length - 6} more</li>
        )}
      </ul>
    </div>
  )
}

/**
 * One section: a row, and its editor when it is the open one.
 *
 * Dragging follows `list-fields.tsx`' shape — the insertion line is drawn as
 * an inset shadow on the row the pointer is over rather than between rows,
 * because a gap between rows is a gap a pointer falls through.
 */
function SectionRow({
  section, number, open, lone, onOpen, onTitle, onBody, onOpensFirst, onRemove, onMove,
}: {
  section: SectionDraft
  number: number
  open: boolean
  /**
   * The only section there is.
   *
   * A grip with nothing to reorder and an **Opens first** chip with nothing
   * else that could open are both controls answering a question nobody can
   * ask — on a legacy document, which is a panel that already has a banner on
   * it, they read as settings somebody chose.
   */
  lone: boolean
  onOpen: () => void
  onTitle: (title: string) => void
  onBody: (body: TermsDoc) => void
  onOpensFirst: (on: boolean) => void
  onRemove?: () => void
  onMove: (from: string, onto: string) => void
}) {
  const [over, setOver] = useState(false)
  /* The one-line preview under the title: the words, not the markup. */
  const preview = termsToText(section.body).replace(/\s+/g, ' ').trim()

  return (
    <div
      role="listitem"
      draggable={!open && !lone}
      onDragStart={(event) => event.dataTransfer.setData('text/plain', section.key)}
      onDragOver={(event) => { event.preventDefault(); setOver(true) }}
      onDragLeave={() => setOver(false)}
      onDrop={(event) => {
        event.preventDefault()
        setOver(false)
        const from = event.dataTransfer.getData('text/plain')
        if (from) onMove(from, section.key)
      }}
      className={cn('rounded-field border bg-surface',
        open ? 'border-accent-line' : 'border-line',
        over && 'shadow-[inset_0_2px_0_0_rgb(var(--color-accent))]')}
    >
      <div className="flex items-center gap-1.5 px-2 py-1.5">
        {lone
          ? <span aria-hidden className="w-[14px] shrink-0" />
          : (
            <span
              aria-hidden
              className={cn('shrink-0 text-text-faint',
                !open && 'cursor-grab active:cursor-grabbing')}
            >
              <IconGrip size={14} />
            </span>
          )}
        <span className="tnum w-4 shrink-0 text-[11.5px] font-semibold text-text-faint">
          {number}
        </span>

        <button
          type="button"
          aria-expanded={open}
          onClick={onOpen}
          className="flex min-h-[34px] min-w-0 flex-1 items-center gap-2 rounded-field
                     px-1 text-left"
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[12.5px] font-semibold text-text">
              {section.title.trim() || `Section ${number}`}
            </span>
            {!open && (
              <span className="block truncate text-[11px] text-text-muted">
                {preview || 'Nothing written yet'}
              </span>
            )}
          </span>
          {section.opensFirst && !lone && (
            <span className="shrink-0 rounded-chip bg-accent-soft px-1.5 py-px text-[10.5px]
                             font-semibold text-accent-dark">
              Opens first
            </span>
          )}
          <span className="shrink-0 text-text-faint">
            {open ? <IconChevronDown size={15} /> : <IconChevronRight size={15} />}
          </span>
        </button>
      </div>

      {open && (
        <div className="flex flex-col gap-2.5 border-t border-line-soft px-3 pb-3 pt-2.5">
          <Field label="Title" hint="the row the customer taps">
            {({ id }) => (
              <TextInput
                id={id}
                value={section.title}
                placeholder="Payment terms"
                onChange={(event) => onTitle(event.target.value)}
              />
            )}
          </Field>

          <RichEditor
            value={section.body}
            onChange={onBody}
            placeholder="What this section says…"
            minHeight={200}
          />

          {!lone && (
            <SwitchRow
              label="Open this section first"
              sub="the one section that is already open when the proposal loads"
              checked={section.opensFirst}
              onChange={onOpensFirst}
            />
          )}

          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              className="flex h-8 items-center gap-1.5 self-start rounded-field px-1.5
                         text-[12px] font-semibold text-danger hover:bg-danger-bg"
            >
              <IconTrash size={14} />
              Delete section
            </button>
          )}
        </div>
      )}
    </div>
  )
}
